import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import AuthService from "../services/AuthService";
import { hasSession, clearTokens } from "./tokenStore";

const AuthContext = createContext({
  user: null,
  isReady: false,
  isAuthenticated: false,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  refresh: async () => null,
  hasPermission: () => false,
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isReady, setIsReady] = useState(false);

  const refresh = useCallback(async () => {
    if (!hasSession()) {
      setUser(null);
      return null;
    }
    try {
      const me = await AuthService.getMe();
      setUser(me);
      return me;
    } catch (error) {
      setUser(null);
      throw error;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (hasSession()) await refresh();
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    await AuthService.login({ email, password });
    const me = await AuthService.getMe();
    setUser(me);
    return me;
  }, []);

  const register = useCallback((payload) => AuthService.register(payload), []);

  const logout = useCallback(async () => {
    await AuthService.logout();
    clearTokens();
    setUser(null);
    window.location.assign("/login");
  }, []);

  const hasPermission = useCallback(
    (permission) => Array.isArray(user?.permissions) && user.permissions.includes(permission),
    [user]
  );

  const value = useMemo(
    () => ({
      user,
      isReady,
      isAuthenticated: !!user,
      login,
      register,
      logout,
      refresh,
      hasPermission,
    }),
    [user, isReady, login, register, logout, refresh, hasPermission]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
